<template>
  <div class="uber-shell" ref="shell" :style="shellStyle">
    <div class="alert-strip" v-if="alerts.length > 0">
      <div class="alert-row" v-for="alert in alerts" :key="alert.key">
        <i class="fas fa-exclamation-triangle"></i>
        <span class="alert-text">
          <strong>{{ alert.label }}</strong>
          &mdash; Trip #{{ alert.tripId }}
          <template v-if="alert.guest"> for {{ alert.guest }}</template>
        </span>
        <v-btn small text class="alert-action" @click="goToAttention(alert)">Review</v-btn>
        <v-btn small icon class="alert-close" @click="dismiss(alert.key)">
          <i class="fas fa-times"></i>
        </v-btn>
      </div>
    </div>

    <v-tabs height="60px" show-arrows v-model="currentTab" background-color="#434343" color="#d59e1d">
      <v-tab :href="`#${TAB.CREATE}`">To be Booked</v-tab>
      <v-tab :href="`#${TAB.ATTENTION}`">
        Attention Required
        <span class="attention-badge" v-if="attentionCount > 0">{{ attentionCount }}</span>
      </v-tab>
      <v-tab :href="`#${TAB.SCHEDULED}`">Scheduled Trips</v-tab>
      <v-tab :href="`#${TAB.ONGOING}`">Ongoing Trips</v-tab>
      <v-tab :href="`#${TAB.COMPLETED}`">Completed Trips</v-tab>
      <v-tab :href="`#${TAB.CANCELLED}`">Cancelled Trips</v-tab>

      <div class="feed-state" :class="feedClass">
        <span class="feed-dot"></span>
        {{ feedLabel }}
      </div>
    </v-tabs>

    <v-tabs-items v-model="currentTab" ref="tabItems">
      <v-tab-item :id="TAB.CREATE" class="tab-outer" transition="fade-transition" reverse-transition="fade-transition">
        <CreateTrip
          v-if="currentTab === TAB.CREATE"
          :rebookFrom="rebookFrom"
          @open-details="openDetails"
          @created="onTripCreated"
        />
      </v-tab-item>

      <v-tab-item :id="TAB.ATTENTION" class="tab-outer" transition="fade-transition" reverse-transition="fade-transition">
        <!-- Table layout rather than the list/details split: these rows exist
             to be acted on, and re-booking happens in place. -->
        <AttentionTrips
          v-if="currentTab === TAB.ATTENTION"
          :focusTrip="focusTrip"
          :refreshSignal="listRefresh"
          @open-details="openDetails"
          @rebooked="onRebooked"
          @count="attentionCount = $event"
        />
      </v-tab-item>

      <v-tab-item :id="TAB.SCHEDULED" class="tab-outer" transition="fade-transition" reverse-transition="fade-transition">
        <UberTripList
          v-if="currentTab === TAB.SCHEDULED"
          listType="scheduled"
          :refreshSignal="listRefresh"
          @open-details="openDetails"
          @cancelled="onDetailsCancelled"
        />
      </v-tab-item>

      <v-tab-item :id="TAB.ONGOING" class="tab-outer" transition="fade-transition" reverse-transition="fade-transition">
        <UberTripList
          v-if="currentTab === TAB.ONGOING"
          listType="ongoing"
          :refreshSignal="listRefresh"
          @open-details="openDetails"
          @cancelled="onDetailsCancelled"
        />
      </v-tab-item>

      <v-tab-item :id="TAB.COMPLETED" class="tab-outer" transition="fade-transition" reverse-transition="fade-transition">
        <UberTripList
          v-if="currentTab === TAB.COMPLETED"
          listType="completed"
          :refreshSignal="listRefresh"
          @open-details="openDetails"
        />
      </v-tab-item>

      <v-tab-item :id="TAB.CANCELLED" class="tab-outer" transition="fade-transition" reverse-transition="fade-transition">
        <UberTripList
          v-if="currentTab === TAB.CANCELLED"
          listType="cancelled"
          :refreshSignal="listRefresh"
          @open-details="openDetails"
        />
      </v-tab-item>
    </v-tabs-items>

    <!--
      One drawer for the whole shell, deliberately OUTSIDE v-tabs-items. Nesting
      it inside a tab panel was the only structural difference from the working
      To be Booked tab.
    -->
    <!-- Opens from the left, matching the standard trip details drawer in
         ToBeAssigned.vue - it has no `right` prop either. -->
    <v-navigation-drawer v-model="detailsDrawer" temporary width="1200" absolute>
      <UberTripDetails
        v-if="detailsDrawer"
        :tripId="detailsTripId"
        closable
        @closeTripDetails="detailsDrawer = false"
        @cancelled="onDetailsCancelled"
      />
    </v-navigation-drawer>
  </div>
</template>

<script>
import CreateTrip from "./CreateTrip.vue";
import UberTripList from "./UberTripList.vue";
import AttentionTrips from "./AttentionTrips.vue";
import UberTripDetails from "./UberTripDetails.vue";
import { UBER_TAB } from "@/config/portalMode";
import { statusLabel } from "@/config/uberStatus";
import uberRealtime from "@/services/uberRealtime";
import axios from "@/axios/axios-global";

export default {
  name: "UberTrips",
  components: { CreateTrip, UberTripList, AttentionTrips, UberTripDetails },
  props: {
    defaultTab: {
      type: String,
      default: UBER_TAB.CREATE,
    },
  },
  data() {
    return {
      TAB: UBER_TAB,
      currentTab: this.defaultTab,
      alerts: [],
      attentionCount: 0,
      focusTrip: null,
      rebookFrom: null,
      feedTransport: null,
      unsubscribe: null,
      // Measured rather than assumed: the app bar height and the alert strip
      // both vary, and a hardcoded calc() left dead space at the bottom.
      contentHeight: null,

      // The details drawer lives here, not inside a tab panel.
      detailsDrawer: false,
      detailsTripId: 0,
      // Bumped to tell the active list to re-fetch, e.g. after a cancel.
      listRefresh: 0,
    };
  },
  computed: {
    /**
     * Published as a CSS variable rather than a height on the shell, so each tab
     * body can size itself without the height having to resolve through
     * Vuetify's window elements in between.
     */
    shellStyle() {
      return this.contentHeight ? { "--uber-tab-height": `${this.contentHeight}px` } : {};
    },
    feedLabel() {
      if (this.feedTransport === "socket") return "Live";
      if (this.feedTransport === "poll") return "Auto-refreshing";
      return "Connecting";
    },
    feedClass() {
      return {
        "feed-live": this.feedTransport === "socket",
        "feed-poll": this.feedTransport === "poll",
      };
    },
  },
  watch: {
    defaultTab(newVal) {
      this.currentTab = newVal;
    },
    // The alert strip sits above the tabs, so showing or hiding it moves the
    // shell and changes how much room is left below it.
    "alerts.length"() {
      this.$nextTick(this.measureShell);
    },
  },
  mounted() {
    this.measureShell();
    window.addEventListener("resize", this.measureShell);

    this.getAttentionCount();

    this.unsubscribe = uberRealtime.subscribe({
      critical: this.onCritical,
      update: this.onUpdate,
      connection: (state) => {
        this.feedTransport = state.connected ? state.transport : null;
      },
    });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.measureShell);
    if (this.unsubscribe) this.unsubscribe();
  },
  methods: {
    /**
     * Sizes the shell to whatever is left below it on screen.
     *
     * Measured rather than calculated: the app bar height varies with its
     * contents, and the alert strip appears and disappears above the tabs. Any
     * hardcoded offset drifts and leaves a dead grey band at the bottom.
     */
    measureShell() {
      const ref = this.$refs.tabItems;
      const el = ref && ref.$el ? ref.$el : ref;
      if (!el || !el.getBoundingClientRect) return;

      // Measured from the top of the tab content area, so the app bar, the tab
      // strip and the alert strip are all accounted for without being named.
      const top = el.getBoundingClientRect().top;
      this.contentHeight = Math.max(320, Math.floor(window.innerHeight - top));
    },

    openDetails(tripId) {
      this.detailsTripId = tripId;
      this.detailsDrawer = true;
    },

    /** A cancel landed - from the details drawer, or from a list row. */
    onDetailsCancelled() {
      this.detailsDrawer = false;
      // The list owns its own data, so nudge it to re-fetch rather than
      // reaching into the child through $refs.
      this.listRefresh += 1;
      this.getAttentionCount();
    },

    /**
     * A trip has fallen into "no driver available" or "driver cancelled".
     * Raise a persistent strip - ops has to acknowledge it, a toast that
     * auto-dismisses is not enough for a trip that will otherwise be missed.
     */
    onCritical(trip) {
      const key = `${trip.tripId}:${trip.uberStatus}`;
      if (this.alerts.some((alert) => alert.key === key)) return;

      this.alerts.unshift({
        key,
        tripId: trip.tripId,
        status: trip.uberStatus,
        label: statusLabel(trip.uberStatus),
        guest: trip.guest || trip.name || "",
      });
      this.attentionCount += 1;
    },

    onUpdate(trip) {
      // The server sends the badge count with every status change, so it moves
      // both ways - up on a failure, back down once the trip is re-booked.
      if (typeof trip.attentionCount === "number") {
        this.attentionCount = trip.attentionCount;
      }

      // A trip that leaves a critical state clears its own alert.
      const stale = this.alerts.filter(
        (alert) => alert.tripId === trip.tripId && alert.status !== trip.uberStatus
      );
      stale.forEach((alert) => this.dismiss(alert.key));
    },

    dismiss(key) {
      this.alerts = this.alerts.filter((alert) => alert.key !== key);
    },

    goToAttention(alert) {
      this.focusTrip = alert.tripId;
      this.currentTab = UBER_TAB.ATTENTION;
      this.dismiss(alert.key);
    },

    /**
     * Re-booking happens in place on the Attention tab now - no hop to the
     * Create tab. The trip moves to Scheduled once Uber accepts the new
     * request, so send the user there to see it.
     */
    onRebooked() {
      this.getAttentionCount();
      this.currentTab = UBER_TAB.SCHEDULED;
    },

    onTripCreated() {
      this.rebookFrom = null;
      // A freshly booked trip is `pending` until Uber confirms, which puts it
      // on Scheduled - it only reaches Ongoing once a driver accepts.
      this.currentTab = UBER_TAB.SCHEDULED;
      this.getAttentionCount();
    },

    async getAttentionCount() {
      try {
        const response = await axios.post("uber/trip/attention/count", {});
        this.attentionCount = (response.data && response.data.count) || 0;
      } catch (err) {
        this.attentionCount = 0;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
// Deliberately plain: Vuetify manages the window container's height inline and
// hides inactive panels with v-show, so forcing flex onto those internals fought
// the library and pushed the visible panel down the page. The tab bodies size
// themselves from the --uber-tab-height variable published below instead.
.uber-shell {
  position: relative;
}

.alert-strip {
  background: #fff4f4;
  border-left: 4px solid #c62828;
  border-radius: 4px;
  margin-bottom: 10px;
  overflow: hidden;
}

.alert-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  color: #8e1c1c;
  font-size: 14px;
  border-bottom: 1px solid rgba(198, 40, 40, 0.12);

  &:last-child {
    border-bottom: none;
  }

  i {
    font-size: 15px;
  }
}

.alert-text {
  flex: 1;
}

.alert-action {
  color: #c62828 !important;
  font-weight: 600;
}

.alert-close {
  color: #8e1c1c !important;
}

.attention-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  margin-left: 8px;
  border-radius: 10px;
  background: #c62828;
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
}

.feed-state {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  padding: 0 18px;
  color: #b9b9b9;
  font-size: 12px;
  align-self: center;
  white-space: nowrap;
}

.feed-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #8a8a8a;
}

.feed-live .feed-dot {
  background: #4caf50;
  box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.6);
  animation: pulse 2s infinite;
}

.feed-poll .feed-dot {
  background: #d59e1d;
}

@keyframes pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.6);
  }
  70% {
    box-shadow: 0 0 0 6px rgba(76, 175, 80, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
}

.tab-outer {
  background: #f4f4f4;
}
</style>

